import { useState, useEffect } from 'react';
import axios from 'axios';
import { Newspaper, Calendar, Tag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultNews = [
  {
    id: '1',
    title: 'UIEHEC Officially Established in Aachen, Germany',
    summary: 'The Union of International Education and Higher Education Centers has been officially founded, bringing together educational institutions worldwide.',
    content: 'We are pleased to announce the official establishment of UIEHEC in Aachen, Germany.',
    category: 'Official Announcements',
    created_at: '2025-01-15',
    image_url: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800'
  },
  {
    id: '2',
    title: 'New Partnership with Global Power Union',
    summary: 'UIEHEC establishes strategic partnership with GPU to expand international education initiatives.',
    content: 'A new era of cooperation begins as UIEHEC and Global Power Union sign partnership agreement.',
    category: 'Partnership News',
    created_at: '2025-01-10',
    image_url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800'
  },
  {
    id: '3',
    title: 'Call for Membership Applications Now Open',
    summary: 'Educational institutions worldwide are invited to apply for UIEHEC membership.',
    content: 'We are now accepting membership applications from universities, colleges, and educational organizations.',
    category: 'Official Announcements',
    created_at: '2025-01-05',
    image_url: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800'
  },
];

const categories = [
  'All',
  'Official Announcements',
  'Press Releases',
  'Partnership News',
  'Interviews and Articles',
  'UIEHEC Bulletin'
];

export default function NewsPage() {
  const [news, setNews] = useState(defaultNews);
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news`);
        if (response.data.length > 0) {
          setNews(response.data);
        }
      } catch (error) {
        console.error('Error fetching news:', error);
      }
    };
    fetchNews();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const filteredNews = selectedCategory === 'All' 
    ? news 
    : news.filter(n => n.category === selectedCategory);

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-[#0E285B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Newspaper className="h-6 w-6 text-[#F5A623]" />
              <span className="text-[#F5A623] font-medium uppercase tracking-wider text-sm">
                News & Media
              </span>
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              News and Media
            </h1>
            <p className="text-slate-300">
              Stay up to date with the latest developments related to UIEHEC and its members.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                  selectedCategory === category
                    ? 'bg-[#0E285B] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredNews.map((item, index) => (
              <article 
                key={item.id} 
                className="bg-white rounded-sm overflow-hidden shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                data-testid={`news-card-${index}`}
              >
                <div className="h-48 overflow-hidden bg-slate-100">
                  {item.image_url ? (
                    <img 
                      src={item.image_url} 
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Newspaper className="h-16 w-16 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-[#F5A623] bg-[#F5A623]/10 px-2 py-1 rounded">
                      <Tag className="h-3 w-3" />
                      {item.category}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <Calendar className="h-3 w-3" />
                      {formatDate(item.created_at)}
                    </span>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#0E285B] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {item.summary}
                  </p>
                </div>
              </article>
            ))}
          </div>

          {filteredNews.length === 0 && (
            <div className="text-center py-12">
              <Newspaper className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No news articles available in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
